import { BaseAPI, IOptions } from "app/common/BaseAPI";
import typeSuite from "app/common/GristImportAPI-ti";

import { CheckerT, createCheckers } from "ts-interface-checker";

/**
 * Client for the Grist-to-Grist import endpoint of one destination document, under
 * `/api/docs/:docId/import/grist` (see ImportFromGrist.ts for the route this wraps).
 *
 * This lives in ext/ rather than on `DocAPI` because importing from another Grist document
 * is not part of grist-core.
 */

/**
 * A source table to import, and where to import it.
 *
 * Source tables with no entry in {@link ImportFromGristPost.tables} are skipped.
 */
export interface ImportFromGristTableMapping {
  /** Id of the table in the source document. */
  sourceTableId: string;
  /**
   * Id of an existing table in the destination document to import into, matching source
   * records to the rows an earlier import created for them. Omit to create a new table.
   */
  destTableId?: string;
  /** Create the table and columns, but import no data. */
  structureOnly?: boolean;
}

export interface ImportFromGristPost {
  /** The urlId or docId of the source document. Must be on the same installation. */
  sourceDocId: string;
  /** Must not be empty. */
  tables: ImportFromGristTableMapping[];
  /**
   * Remove ALL of the document's pre-existing tables once the source tables have been
   * imported, effectively replacing the document's contents with the imported tables.
   * When importing into a brand-new document, this removes the initial `Table1`.
   */
  replaceExistingTables?: boolean;
}

export interface ImportFromGristResult {
  /** Destination tables created or imported into, per non-skipped source table. */
  tables: { sourceTableId: string; tableId: string }[];
  warnings: string[];
}

export const ImportFromGristPostChecker =
  createCheckers(typeSuite).ImportFromGristPost as CheckerT<ImportFromGristPost>;

ImportFromGristPostChecker.setReportedPath("body");

// The interface exposed to the client via REST API, and also implemented by GristImportAPIImpl.
export interface GristImportAPI {
  importFromGrist(params: ImportFromGristPost): Promise<ImportFromGristResult>;
}

export class GristImportAPIImpl extends BaseAPI implements GristImportAPI {
  constructor(private _docBaseUrl: string, options: IOptions = {}) {
    super(options);
  }

  public async importFromGrist(params: ImportFromGristPost): Promise<ImportFromGristResult> {
    return this.requestJson(`${this._docBaseUrl}/import/grist`, {
      body: JSON.stringify(params),
      method: "POST",
    });
  }
}
